import { Checkbox } from '@servicetim/ui/components';
import { cn } from '@servicetim/ui/lib';
import { type ReactNode, useId } from 'react';

export interface CheckListOption {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface CheckListProps {
  options: readonly CheckListOption[];
  value: readonly string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  emptyText?: string;
}

/** Grid of labelled checkboxes (a fieldset); the value is the list of checked option values. */
export function CheckList({
  options,
  value,
  onChange,
  disabled,
  className,
  emptyText = 'Nothing to choose from.',
  ...aria
}: CheckListProps) {
  const id = useId();
  if (options.length === 0) {
    return <p className="text-fg-muted text-sm">{emptyText}</p>;
  }
  return (
    <fieldset
      aria-label={aria['aria-label']}
      className={cn('grid gap-1.5 sm:grid-cols-2', className)}
    >
      {aria['aria-label'] ? <legend className="sr-only">{aria['aria-label']}</legend> : null}
      {options.map((option) => {
        const checked = value.includes(option.value);
        const inputId = `${id}-${option.value}`;
        return (
          <div
            key={option.value}
            className={cn(
              'flex items-start gap-2 rounded-md border border-border px-2 py-1.5 text-sm',
              (disabled || option.disabled) && 'opacity-60',
            )}
          >
            <Checkbox
              id={inputId}
              checked={checked}
              disabled={disabled || option.disabled}
              onCheckedChange={(next) =>
                onChange(
                  next === true
                    ? [...value, option.value]
                    : value.filter((v) => v !== option.value),
                )
              }
              className="mt-0.5"
            />
            <label htmlFor={inputId} className="min-w-0 flex-1 cursor-pointer">
              <span className="block truncate">{option.label}</span>
              {option.description ? (
                <span className="block truncate text-fg-muted text-xs">{option.description}</span>
              ) : null}
            </label>
          </div>
        );
      })}
    </fieldset>
  );
}
